import { message } from './message'
import { useApiForm, type ApiFormSource, type MutationLike } from './useApiForm'

/**
 * What a save says when the modal has nothing more specific to say.
 *
 * Deliberately neutral: this is the default for both create and edit, and "Your changes
 * have been saved" is wrong for the half of them that just made a record.
 */
const SAVED_MESSAGE = 'Saved.'

/** `useApiForm`'s source plus the reset a modal needs when it closes. */
type ModalFormSource<TPayload> = ApiFormSource<TPayload> & {
  resetForm: () => void
}

/**
 * `useApiForm` for a form that lives in a modal — the submit pipeline plus a `close()` that
 * resets the form before telling the parent, a success toast, and the closing itself.
 * **Every create/edit modal's script is this call**; reach for `useApiForm` directly only
 * for a form that is not in a modal.
 *
 * ```ts
 * // closes on save — the common case
 * const { submit, isSubmitting, close } = useModalForm({
 *   entityForm: websiteForm,
 *   mutation,
 *   onClose: () => emit('close'),
 *   successMessage: 'Website has been saved.'   // optional — defaults to "Saved."
 * })
 *
 * // the parent navigates instead, so it wants the event rather than a close
 * onSaved: (response) => emit('save', response?.data?.data?.id)
 * ```
 *
 * **The form is already reset when `onSaved` runs** — resetting is this hook's job
 * either way, so a caller never repeats it. What `onSaved` decides is only what the
 * parent is told.
 */
export function useModalForm<TPayload, TResponse>(options: {
  entityForm: ModalFormSource<TPayload>
  mutation: MutationLike<TPayload, TResponse>

  /** Tell the parent the modal is done — the route is what actually closes it. */
  onClose: () => void

  /**
   * The toast a successful save shows. **It defaults to "Saved.", so a modal that passes
   * nothing still confirms** — pass a sentence to say something better, a function of the
   * response when the answer decides the wording, and `null` for the rare modal that has to
   * stay silent.
   *
   * Do not toast from `onSaved` instead: that is for what the parent does next, and a modal
   * that says it there ends up hand-writing the close this hook already performs.
   */
  successMessage?: string | null | ((response: TResponse) => string | null)

  /**
   * What to do after a successful submit instead of closing. Omit it and the modal
   * closes, which is what most of them want.
   */
  onSaved?: (response: TResponse) => void

  /** A refused save, after its errors are already on the form — for a reason worth acting on. */
  onError?: (error: unknown) => void

  /** See `useApiForm`. */
  confirm?: () => boolean | Promise<boolean>
}) {
  function close() {
    options.entityForm.resetForm()
    options.onClose()
  }

  const { submit, isSubmitting } = useApiForm({
    entityForm: options.entityForm,
    mutation: options.mutation,

    onSuccess(response) {
      // Before either branch: the toast is about the save, not about what the screen
      // does next, and both branches leave the form reset behind them. `undefined` takes
      // the default; only an explicit `null` opts out.
      const text =
        typeof options.successMessage === 'function'
          ? options.successMessage(response)
          : options.successMessage === undefined
            ? SAVED_MESSAGE
            : options.successMessage

      if (text) message.success(text)

      if (!options.onSaved) return close()

      options.entityForm.resetForm()
      options.onSaved(response)
    },

    onError: options.onError,
    confirm: options.confirm
  })

  return { submit, isSubmitting, close }
}
